<template>
    <AuthLayout>
        <div
            class="mt-8 w-full overflow-hidden bg-white p-6 shadow-xl sm:mt-14 sm:max-w-md sm:rounded-lg"
        >
            <div class="mb-8 mt-[2px] border-b border-gray-200 pb-5">
                <h1 class="text-center text-xl font-bold text-gray-800">
                    {{ $t("vue.create_new_password") }}
                </h1>
            </div>
            <form @submit.prevent="submit">
                <div>
                    <TextInput
                        id="email"
                        v-model="state.form.email"
                        type="email"
                        required
                        autofocus
                        suppress-required-icon
                        :label="$t('vue.email_address')"
                        :placeholder="$t('vue.your_email_address')"
                        name="email"
                        autocomplete="email"
                        :error="getError('email')"
                        @blur="v$.form.email.$touch()"
                    />
                </div>

                <div class="mt-6">
                    <TextInput
                        id="password"
                        v-model="state.form.password"
                        type="password"
                        required
                        suppress-required-icon
                        autocomplete="new-password"
                        name="new-password"
                        :label="$t('vue.set_new_password')"
                        :placeholder="$t('vue.set_your_new_password')"
                        :error="getError('password')"
                        @blur="v$.form.password.$touch()"
                    />
                </div>

                <div class="mt-6">
                    <TextInput
                        id="password_confirmation"
                        v-model="state.form.password_confirmation"
                        type="password"
                        required
                        suppress-required-icon
                        autocomplete="new-password"
                        name="new-password-confirmation"
                        :label="$t('vue.confirm_password')"
                        :placeholder="$t('vue.your_new_password_again_please')"
                        :error="getError('password_confirmation')"
                        @blur="v$.form.password_confirmation.$touch()"
                    />
                </div>

                <div class="mt-10 flex flex-col items-center">
                    <MegaButton :disabled="loading" type="submit"
                        >{{ $t("vue.confirm_new_password") }}
                    </MegaButton>

                    <inertia-link
                        :href="route('login')"
                        class="mt-6 pb-2 text-xs font-medium text-custom-blue-500 hover:text-custom-blue-600"
                        >{{
                            $t("vue.cancel_reseting_your_password")
                        }}</inertia-link
                    >
                </div>
            </form>
        </div>
    </AuthLayout>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from "vue";
import route from "ziggy-js";
import {
    required,
    email as emailRule,
    minLength,
    maxLength,
    helpers,
    sameAs,
} from "@vuelidate/validators";
import { useReCaptcha } from "vue-recaptcha-v3";
import useVuelidate from "@vuelidate/core";
import { router } from "@inertiajs/vue3";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import { useTranslations } from "@/composables/useTranslations";

const props = defineProps({
    email: {
        type: String,
        required: true,
    },
    token: {
        type: String,
        required: true,
    },
});

// Get access to translations
const { t } = useTranslations();

const reCaptcha = useReCaptcha();

const state = reactive({
    form: {
        email: props.email,
        password: "",
        password_confirmation: "",
        token: props.token,
        recaptcha_token: "",
    },
});
const loading = ref(false);
const errors = ref<IInputErrors>({});
const passwordRef = computed(() => state.form.password);

const rules = {
    form: {
        email: { required, emailRule, maxLength: maxLength(255) },
        password: { required, minLength: minLength(8) },
        password_confirmation: {
            required,
            minLength: minLength(8),
            sameAsPassword: helpers.withMessage(
                t("vue.passwords_do_not_match"),
                sameAs(passwordRef),
            ),
        },
    },
};

const v$ = useVuelidate(rules, state);

const submit = async () => {
    v$.value.form.$touch();
    if (v$.value.form.$error) {
        () => undefined;
    } else {
        try {
            loading.value = true;

            let token = undefined;

            try {
                await reCaptcha?.recaptchaLoaded();
                token = await reCaptcha?.executeRecaptcha("resetPassword");

                if (token === undefined) {
                    throw new Error();
                }
            } catch (e) {
                loading.value = false;
                errors.value = {
                    email: t("vue.recaptcha_validation_failed"),
                };
                return;
            }
            state.form.recaptcha_token = token;

            router.post(route("password.update"), state.form, {
                preserveScroll: true,
                onError: (err) => {
                    errors.value = err;
                },
                onFinish: () => {
                    loading.value = false;
                    v$.value.$reset();
                },
            });
        } catch (e) {
            throw new Error(JSON.stringify(e));
        }
    }
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
